import type { BaseQueryFn } from '@reduxjs/toolkit/query'
import type { AxiosRequestConfig, AxiosError } from 'axios'
import { AxiosInstance, PublicAxiosInstance, unwrapEnvelope } from '../../logic/apis'

interface AxiosBaseQueryArgs {
  url: string
  method?: AxiosRequestConfig['method']
  data?: any
  params?: any
  interceptorConfig?: any
}

export const axiosBaseQuery: BaseQueryFn<
  AxiosBaseQueryArgs,
  unknown,
  { status?: number; data?: any; message?: string }
> = async ({ url, method = 'GET', data, params, interceptorConfig }) => {
  try {
    const result = await AxiosInstance({
      url,
      method,
      data,
      params,
      interceptorConfig,
    } as AxiosRequestConfig)
    // AxiosInstance response interceptor already returns response.data.data
    return { data: result }
  } catch (error) {
    const axiosError = error as AxiosError & { apiError?: any }
    return {
      error: {
        status: axiosError.response?.status ?? axiosError.apiError?.statusCode,
        data: axiosError.response?.data,
        message: axiosError.apiError?.message ?? axiosError.message,
      },
    }
  }
}

export interface BaseQueryErrorShape {
  status?: number
  data?: unknown
  message?: string
}

/**
 * Public (unauthenticated) sibling of `axiosBaseQuery`.
 *
 * Rides `PublicAxiosInstance`, which carries NO interceptors — so a call from
 * a logged-out visitor never gets a bearer token attached and a 401 (expired
 * invite / reset token) never triggers the refresh-then-logout redirect. Use
 * it from an endpoint's `queryFn`; the returned shape is identical to
 * `axiosBaseQuery`'s so components handle errors the same way either side.
 */
interface PublicBaseQueryArgs {
  url: string
  method?: AxiosRequestConfig['method']
  data?: unknown
  params?: Record<string, unknown>
}

export const publicBaseQuery = async <T>({
  url,
  method = 'GET',
  data,
  params,
}: PublicBaseQueryArgs): Promise<{ data: T } | { error: BaseQueryErrorShape }> => {
  try {
    const response = await PublicAxiosInstance.request<{ data?: T }>({ url, method, data, params })
    return { data: unwrapEnvelope<T>(response) }
  } catch (error) {
    const axiosError = error as AxiosError<{ message?: string | string[] }>
    const body = axiosError.response?.data
    const rawMessage = body?.message
    return {
      error: {
        status: axiosError.response?.status,
        data: body,
        message: Array.isArray(rawMessage) ? rawMessage.join(', ') : rawMessage ?? axiosError.message,
      },
    }
  }
}
